import { BCMSDate, BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, Branding, DisabledPrompt, useBCMSContext, useModel, useStateManager } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import DateSelect from "components/custom-ui/DateSelect";
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from "components/shared";
import WirePayeeConfirmationWidget from "components/shared/widgets/WirePayeeConfirmationWidget";
import { PrimaryButton, SecondaryButton, UIAmountInput, UICheckbox, UITextInput } from "components/ui";
import Classes, { Account, OneTimeWireTransfer, Payee, WireHistory } from "model";
import React from "react";
import WireFinancialInstitutionForm from "./components/WireFinancialInstitutionForm";
import WirePayeeInformationWidget from "./components/WirePayeeInformationWidget";

import "./styles.css";
import "./mobile.css";



/**
 * @function OneTimeWirePage
 * @return {React.Component}
 * @exported true
 * @description The main component for OneTimeWirePage
 */
export default function OneTimeWirePage(props) {
    const context = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, context);
    const data = { ...getDefaultData(), ...props.data ?? {} };
    const ui = { ...getDefaultUI(), ...props.ui ?? {} };
    const { user, branding} = useModel();

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onSubmittedConfirmationClose: () => {
            BCMSStateManagerCRUD.init(state)
            .update([ "data", "createdOneTimeWireTransfer" ], undefined)
            .update([ "data", "wireTransferSubmitted" ], false)
            .submit(setState);
        },

        onChangeOneTimeWireProp: prop => val => {
            const crud = BCMSStateManagerCRUD.init(state);

            if(prop === "date") crud.update([ "data", "didSetDate" ], true);

            if(prop === "offsetAccount") {
                const selectedOffsetAccount = Account.initByID(val);
                crud.update([ "data", "oneTimeWireTransfer", "offsetAccount" ], selectedOffsetAccount ?? undefined);
            } else {
                crud.update([ "data", "oneTimeWireTransfer", prop ], val);
            }
            
            crud.submit(setState);
        },
        onSubmitClick: () => {
            const defaultOneTimeWireTransfer = BCMSDefaultModel.oneTimeWireTransfer();
            defaultOneTimeWireTransfer.fundingAccount = Account.getDefaultBrandedAccountByType("checking").id;
            defaultOneTimeWireTransfer.initiatedBy = `${user.first} ${user.last}`;
            let oneTimeWireTransferRecord = state.data.oneTimeWireTransfer.record;

            // START: Increment the reference number.
            const allOneTimeWireTransfers = OneTimeWireTransfer.initAll()
                .filter(item => item.id !== undefined)
                .sort((a, b) => parseFloat(b.referenceNumber) - parseFloat(a.referenceNumber))
            let lastReferenceNumber = defaultOneTimeWireTransfer.referenceNumber;
            if (allOneTimeWireTransfers.length > 0) {
                lastReferenceNumber = parseFloat(allOneTimeWireTransfers[0].referenceNumber) + 1;
            }
            // END: Increment the reference number.
            // START: Increment the reference number for single wire.
            const allWireHistory = WireHistory.initAll()
                .filter(item => item.id !== undefined)
                .sort((a, b) => parseFloat(b.referenceNumber) - parseFloat(a.referenceNumber))
            let wireHistoryRefNumber = allWireHistory.referenceNumber;
            if (allWireHistory.length > 0) {
                wireHistoryRefNumber = parseInt(allWireHistory[0].referenceNumber, 10) + 1;
                wireHistoryRefNumber = ("00" + wireHistoryRefNumber).slice(-3);
            }
            // END: Increment the reference number for single wire.
            oneTimeWireTransferRecord = {
                ...oneTimeWireTransferRecord, id: OneTimeWireTransfer.nextID(), referenceNumber: lastReferenceNumber
            };
            const oneTimeWireTransfer = OneTimeWireTransfer.init(oneTimeWireTransferRecord);
            const mergeRecord = BCMSDefaultModel.mergeRecords(oneTimeWireTransfer, defaultOneTimeWireTransfer);

            let wireTransfer = undefined;
            // If true, creates a new one time wire transfer or else a history wire.
            if (branding.options.wire_approvals_abi22 === "true") {

                wireTransfer = OneTimeWireTransfer.init({ ...mergeRecord })
                BCMSRecord.batch([
                    OneTimeWireTransfer.create({ ...mergeRecord })
                ], context, Classes);

            } else {

                const oneTimeWire = OneTimeWireTransfer.init({ ...mergeRecord, referenceNumber: wireHistoryRefNumber });
                wireTransfer = oneTimeWire;

                const payee = Payee.init({
                    ...oneTimeWire.record,
                    id: Payee.nextID(),
                    account: oneTimeWire.fundingAccount.number,
                    nickname: oneTimeWire.name
                })
                const wireHistory = WireHistory.init({
                    ...oneTimeWire.record,
                    id: WireHistory.nextID(),
                    payee: payee,
                    statusDescription: oneTimeWire.status,
                    nickname: oneTimeWire.name,
                    referenceNumber: wireHistoryRefNumber
                })
               

                BCMSRecord.batch([
                    WireHistory.create({ ...wireHistory.record }),
                    Payee.create({ ...payee.record })
                ], context, Classes);
            }

            BCMSStateManagerCRUD.init(state)
            .update([ "data", "createdOneTimeWireTransfer" ], wireTransfer)
            .update([ "data", "wireTransferSubmitted" ], true)
            .update([ "data", "oneTimeWireTransfer" ], getDefaultData().oneTimeWireTransfer)
            .update([ "data", "didSetDate" ], false)
            .submit(setState);

            window.scrollTo(0, 0);
        }
    });

    return (
        <PageContainer className="one-time-wire-page" state={ { data, ui } } controller={ controller }>
            <PageContent/>
        </PageContainer>
    )
}

export const getDefaultData = () => ({
    oneTimeWireTransfer: OneTimeWireTransfer.init({
        fundingAccount: undefined,
        date: undefined,
        payeeType: "domestic",
        financialInstitutionNumberType: "aba",
        receivingInstitutionNumberType: "aba",
        intermediaryInstitutionNumberType: "aba"
    }),
    didSetDate: false,
    createdOneTimeWireTransfer: undefined,
    wireTransferSubmitted: false,
    offsetAccounts: Account.initAllAsset()
});

export const getDefaultUI = () => ({
    // Opens all the dropdowns in the page.
    /*dateSelectIsActive: true,
    offsetAccountSelectIsActive: true,
    stateSelectIsActive: true,
    countrySelectIsActive: true,
    financialInstitutionNumberTypeSelectIsActive: true,
    financialInstitutionStateSelectIsActive: true,
    financialInstitutionCountrySelectIsActive: true,
    receivingInstitutionNumberTypeSelectIsActive: true,
    receivingInstitutionStateSelectIsActive: true,
    receivingInstitutionCountrySelectIsActive: true,
    intermediaryInstitutionNumberTypeSelectIsActive: true,
    intermediaryInstitutionStateSelectIsActive: true,
    intermediaryInstitutionCountrySelectIsActive: true,*/
});

function PageContent(props) { 
    const { data, ui, onChangeOneTimeWireProp, onSubmitClick, onSubmittedConfirmationClose } = useStateManager();
    const action = {
        onChangeOneTimeWireProp,
        onSubmittedConfirmationClose
    };
    const offsetAccountSelectIsActive = ui.offsetAccountSelectIsActive;
    const offsetAccounts = data.offsetAccounts ?? [];
    const oneTimeWireTransfer = data.oneTimeWireTransfer;
    const selectedOffsetAccount = oneTimeWireTransfer.fundingAccount ?? { id: "prompt" };
    const amount = oneTimeWireTransfer.amount;
    // Past days are disabled.
    const disableDay = date => date.isBefore(BCMSDate.init());
    const offsetAccountRows = offsetAccounts.reduce((acc, item, i) => {
        if(i === 0) acc.push(<CustomSelectItem key={ "prompt" } value={ "prompt" }>- Select a Funding Account -</CustomSelectItem>);
        
        let accountName = ``;
        if(item.type === "loan") {
            accountName = `${ item.number } - ${ item.nickname } (Current ${ item.currentBalance.format() })`;
        } else if(item.type === "loc") {
            accountName = `${ item.number } - ${ item.nickname } (Available ${ item.availableCredit.format() })`;
        } else {
            accountName = `${ item.number } - ${ item.nickname } (Available ${ item.availableBalance.format() })`;
        }

        acc.push(
            <CustomSelectItem key={ item.id } value={ item.id }>{ accountName } </CustomSelectItem>
        );
        return acc;
    }, []);
    let requiresReceivingFinancialInstitutionDetails = oneTimeWireTransfer.requiresReceivingFinancialInstitutionDetails ?? false;
    const requiresIntermediaryFinancialInstitutionDetails = oneTimeWireTransfer.requiresIntermediaryFinancialInstitutionDetails ?? false;

    let disableRequiresReceivingFinancialInstitutionDetails = false;
    if(oneTimeWireTransfer.payeeType === "international" && oneTimeWireTransfer.financialInstitutionNumberType === "aba") {
        requiresReceivingFinancialInstitutionDetails = true;
        disableRequiresReceivingFinancialInstitutionDetails = true;
    }

    return (
        <PageContentView>
            { data.wireTransferSubmitted && 
            <WirePayeeConfirmationWidget data={ data } action={ action } />
            }
                <CenterContentContainer>
                    <ContentHeaderView text="One-Time Wire" />
                    <FormContainer className="small-form-width">
                        <FormRowView text="* Funding Account">
                            <CustomSelect active={ offsetAccountSelectIsActive } value={ selectedOffsetAccount.id }
                            onChange={ onChangeOneTimeWireProp("fundingAccount") }>
                                { offsetAccountRows }
                            </CustomSelect>
                        </FormRowView>
                        <FormRowView text="* Amount">
                            <UIAmountInput value={ amount.amount } onChange={ onChangeOneTimeWireProp("amount") } />
                        </FormRowView>
                        <FormRowView text="* Date">
                            <DateSelect active={ ui.dateSelectIsActive } value={ data.didSetDate ? oneTimeWireTransfer.date : undefined } disableDay={ disableDay } onChange={ onChangeOneTimeWireProp("date") } />
                        </FormRowView>
                        <Branding id="reasonforpayment_abi22" value="true" default="true">
                            <FormRowView text="Reason for Payment">
                                <UITextInput value={ oneTimeWireTransfer.reasonForPayment } onChange={ onChangeOneTimeWireProp("reasonForPayment") } />
                            </FormRowView>
                        </Branding>
                        <Branding id="memo_abi22" value="true">
                            <FormRowView text="Memo">
                                <UITextInput value={ oneTimeWireTransfer.memo } onChange={ onChangeOneTimeWireProp("memo") } />
                            </FormRowView>
                        </Branding>
                    </FormContainer>

                    <WirePayeeInformationWidget data={ data } ui={ ui } action={ action } />

                    <div className="payee-financial-institution financial-institution-container">
                        <h2 className="header-text">Payee Financial Institution</h2>
                        <WireFinancialInstitutionForm className="payee-financial-institution-form" data={ data } ui={ ui } action={ action } sectionType="financialInstitution" />
                    </div>

                    <div className="receiving-financial-institution-container financial-institution-container">
                        <h2 className="header-text">Receiving Financial Institution (optional)</h2>
                        {/* Wire financial institution form is shown if "Show Details" checkbox is checked */}
                        <UICheckbox disabled={ disableRequiresReceivingFinancialInstitutionDetails } label="Show Details" value={ requiresReceivingFinancialInstitutionDetails } onChange={ onChangeOneTimeWireProp("requiresReceivingFinancialInstitutionDetails") } />
                        { requiresReceivingFinancialInstitutionDetails &&
                        <WireFinancialInstitutionForm className="receiving-financial-institution-form" data={ data } ui={ ui } action={ action } sectionType="receivingInstitution" />
                        }
                        </div>
                    <div className="intermediary-financial-institution-container financial-institution-container">
                        <h2 className="header-text">Intermediary Financial Institution (optional)</h2>
                        {/* Wire financial institution form is shown if "Show Details" checkbox is checked */}
                        <UICheckbox label="Show Details" value={ requiresIntermediaryFinancialInstitutionDetails } onChange={ onChangeOneTimeWireProp("requiresIntermediaryFinancialInstitutionDetails") } />
                        { requiresIntermediaryFinancialInstitutionDetails && 
                        <WireFinancialInstitutionForm className="receiving-financial-institution-form" data={ data } ui={ ui } action={ action } sectionType="intermediaryInstitution" />
                        }
                    </div>

                    <div className="flex-container flex-horizontal align-center button-container">
                        <DisabledPrompt>
                            <SecondaryButton text="Cancel" />
                        </DisabledPrompt>
                        <PrimaryButton text="Submit" onClick={ onSubmitClick } />
                    </div>
                </CenterContentContainer>
            </PageContentView>
    );
}
